import "./bootstrap";

import "@vime/core/themes/default.css";

import { BrowserTracing } from "@sentry/tracing";
import { createApp, h } from "vue";
import ClickOutside from "@js/directives/click-outside";
import Icon from "@icons/icon.vue";
import {
    scorecardTypes,
    status,
    strokesGainedFacets,
    userTypes,
    userStatuses,
} from "./shared/consts";
import { Head, Link, createInertiaApp } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { InertiaProgress } from "@inertiajs/progress";
import { setErrorReporter } from "@global/composables/report-error";
import { suppressAbortedVisitErrors } from "./shared/inertia-network-errors";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import {
    clearStaleChunkGuard,
    reloadOnceForStaleChunk,
} from "./shared/stale-chunk-reload";
import * as Sentry from "@sentry/vue";
import { vfmPlugin } from "vue-final-modal";
import { ZiggyVue } from "ziggy-vue";
import Vue3TouchEvents from "vue3-touch-events";
import VueGtag from "vue-gtag";

let init = function (vueDir, Layout, domainConstants = null) {
    createInertiaApp({
        resolve: (name) => {
            const page = resolvePageComponent(
                `./components/${vueDir}/${name}.vue`,
                import.meta.glob("./components/**/*.vue")
            ).catch(reloadOnceForStaleChunk);

            // Add default layout file to all pages
            page.then((module) => {
                clearStaleChunkGuard();
                module.default.layout = module.default.layout || Layout;
            });
            page.layout = page.layout || Layout;

            return page;
        },
        setup({ el, App, props, plugin }) {
            const app = createApp({ render: () => h(App, props) })
                .use(plugin)
                .use(ZiggyVue)
                .use(vfmPlugin)
                .use(Vue3TouchEvents)
                .use(VueGtag, {
                    config: {
                        // id: 'AW-924070550',
                        // id: 'GTM-TFRXX93',
                        id: "G-VKBEG572EJ", // this tag was used by some CRS items
                        // Disable automatic pageview tracking as we're already doing that elsewhere
                        send_page_view: false,
                    },
                })
                .component("Head", Head)
                .component("InertiaLink", Link)
                .component("Icon", Icon)
                .directive("click-outside", ClickOutside);

            // Can be removed with Vue 3.3. Used to auto unwrap refs and avoid using ref.value, allows for providing/injecting reactive data
            // app.config.unwrapInjectedRef = true;

            // Apply constants
            app.config.globalProperties.$scorecardTypes = scorecardTypes;
            app.config.globalProperties.$status = status;
            app.config.globalProperties.$strokesGainedFacets =
                strokesGainedFacets;
            app.config.globalProperties.$userTypes = userTypes;
            app.config.globalProperties.$userStatuses = userStatuses;
            app.config.globalProperties.$constants = domainConstants;

            Sentry.init({
                app,
                dsn: import.meta.env.VITE_SENTRY_LARAVEL_DSN,
                environment: import.meta.env.VITE_APP_ENV,
                integrations: [
                    new BrowserTracing({
                        //routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                        tracePropagationTargets: [
                            "localhost",
                            "swingu.com",
                            /^\//,
                        ],
                    }),
                ],
                logErrors: true,
                // This rate governs most server tracing too, not just the browser: the
                // propagation targets above send trace headers on our own XHRs, and
                // Laravel continues a sampled parent instead of applying its own
                // SENTRY_TRACES_SAMPLE_RATE. Size it against the org's span quota.
                tracesSampleRate: 0.03,
                // Off: a span per component mount/update was ~60% of all spans and
                // exhausted the span quota on its own.
                trackComponents: false,
                ignoreErrors: [
                    /\.end is not a function/,
                    // Browser noise, not a fault: a ResizeObserver callback that changes
                    // layout leaves notifications for the next frame, and Chrome reports
                    // that as an uncaught error. Our own observers remeasure a frame
                    // later to avoid it, but third-party ones (Google Maps, Headless UI)
                    // are beyond reach and nothing here is actionable.
                    /ResizeObserver loop/,
                    // Benign teardown race inside the third-party Par One SDK.
                    // We can't patch their SDK, and it has no user impact.
                    /videoElement\.value\.src/,
                ],
            });

            // Hand Sentry to code that must not import it. Globally registered
            // components are shared with entrypoints that never initialise Sentry,
            // so they report through this seam instead of bundling the SDK.
            setErrorReporter((error, context) =>
                Sentry.captureException(error, context)
            );

            app.mount(el);
        },
    });

    InertiaProgress.init({
        color: getComputedStyle(document.documentElement).getPropertyValue(
            "--page-status-indicator"
        ),
        showSpinner: false,
    });

    // Drop the unhandled rejection from visits the iOS webview aborted.
    suppressAbortedVisitErrors(Inertia);
};

export { init };
